import React from 'react'
import PropTypes from 'prop-types'
import { Card, Row, Col, Typography, Statistic, Empty, Space, Tag, Divider } from 'antd'
import { MobileOutlined, TranslationOutlined, AppstoreOutlined } from '@ant-design/icons'
import { Pie } from 'react-chartjs-2'
import styled from 'styled-components'

const { Title, Text } = Typography

const StyledCard = styled(Card)`
  margin-bottom: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border-radius: 8px;

  .ant-card-head {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-bottom: none;
    border-radius: 8px 8px 0 0;

    .ant-card-head-title {
      color: white;
      font-weight: 600;
    }
  }
`

const ChartContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
  position: relative;
`

const StatCard = styled(Card)`
  text-align: center;
  height: 100%;

  &.platforms {
    border-left: 4px solid #52c41a;
  }

  &.versions {
    border-left: 4px solid #1890ff;
  }

  &.languages {
    border-left: 4px solid #722ed1;
  }
`

const AppDetails = ({ data, chartOptions, loading }) => {
  if (loading) {
    return <Card loading />
  }

  if (!data || !Object.keys(data).length) {
    return (
      <Empty
        description="Нет данных для отображения"
        image={Empty.PRESENTED_IMAGE_SIMPLE}
      />
    )
  }

  const getTotalCount = (chartData) => {
    return chartData?.datasets?.[0]?.data?.reduce((sum, val) => sum + val, 0) || 0
  }

  const getTopItems = (chartData, limit = 3) => {
    if (!chartData?.labels || !chartData?.datasets?.[0]?.data) return []

    const items = chartData.labels.map((label, index) => ({
      label: label === 'null' ? 'Не определено' : label,
      value: chartData.datasets[0].data[index]
    }))

    return items
      .sort((a, b) => b.value - a.value)
      .slice(0, limit)
  }

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <Title level={2} style={{ color: '#1890ff', marginBottom: 8 }}>
          <AppstoreOutlined /> Статистика приложений
        </Title>
        <Text type="secondary">
          Анализ версий, платформ и языков пользователей приложения
        </Text>
      </div>

      {/* Summary Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col span={8}>
          <StatCard className="platforms" size="small">
            <Statistic
              title={
                <div>
                  <MobileOutlined style={{ color: '#52c41a' }} /> Платформы
                </div>
              }
              value={getTotalCount(data.platforms)}
              suffix="пользователей"
              valueStyle={{ color: '#52c41a' }}
            />
          </StatCard>
        </Col>

        <Col span={8}>
          <StatCard className="versions" size="small">
            <Statistic
              title={
                <div>
                  <AppstoreOutlined style={{ color: '#1890ff' }} /> Версии
                </div>
              }
              value={getTotalCount(data.versions)}
              suffix="пользователей"
              valueStyle={{ color: '#1890ff' }}
            />
          </StatCard>
        </Col>

        <Col span={8}>
          <StatCard className="languages" size="small">
            <Statistic
              title={
                <div>
                  <TranslationOutlined style={{ color: '#722ed1' }} /> Языки
                </div>
              }
              value={getTotalCount(data.languages)}
              suffix="пользователей"
              valueStyle={{ color: '#722ed1' }}
            />
          </StatCard>
        </Col>
      </Row>

      {/* Charts Section */}
      <Row gutter={[16, 16]}>
        {/* Platforms Chart */}
        <Col span={24}>
          <StyledCard title="Распределение по платформам" size="small">
            <Row gutter={16}>
              <Col span={16}>
                <ChartContainer>
                  {data.platforms ? (
                    <Pie data={data.platforms} options={chartOptions} />
                  ) : (
                    <Empty description="Нет данных по платформам" />
                  )}
                </ChartContainer>
              </Col>
              <Col span={8}>
                <div>
                  <Title level={4}>Топ платформы:</Title>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    {getTopItems(data.platforms).map((item, index) => (
                      <div key={index} style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Text>{item.label}</Text>
                        <Tag color={index === 0 ? 'green' : index === 1 ? 'blue' : 'orange'}>
                          {item.value}
                        </Tag>
                      </div>
                    ))}
                  </Space>
                </div>
              </Col>
            </Row>
          </StyledCard>
        </Col>

        {/* Languages and Versions */}
        <Col span={12}>
          <StyledCard title="Распределение по языкам" size="small">
            <ChartContainer>
              {data.languages ? (
                <Pie data={data.languages} options={chartOptions} />
              ) : (
                <Empty description="Нет данных по языкам" />
              )}
            </ChartContainer>

            <Divider />

            <div>
              <Text strong>Топ языки:</Text>
              <div style={{ marginTop: 8 }}>
                {getTopItems(data.languages).map((item, index) => (
                  <Tag
                    key={index}
                    color={index === 0 ? 'green' : index === 1 ? 'blue' : 'orange'}
                    style={{ marginBottom: 4 }}
                  >
                    {item.label}: {item.value}
                  </Tag>
                ))}
              </div>
            </div>
          </StyledCard>
        </Col>

        <Col span={12}>
          <StyledCard title="Распределение по версиям" size="small">
            <ChartContainer>
              {data.versions ? (
                <Pie data={data.versions} options={chartOptions} />
              ) : (
                <Empty description="Нет данных по версиям" />
              )}
            </ChartContainer>

            <Divider />

            <div>
              <Text strong>Топ версии:</Text>
              <div style={{ marginTop: 8 }}>
                {getTopItems(data.versions).map((item, index) => (
                  <Tag
                    key={index}
                    color={index === 0 ? 'green' : index === 1 ? 'blue' : 'orange'}
                    style={{ marginBottom: 4 }}
                  >
                    {item.label}: {item.value}
                  </Tag>
                ))}
              </div>
            </div>
          </StyledCard>
        </Col>
      </Row>

      {/* Interactive Instructions */}
      <Card style={{ marginTop: 16, background: '#f6ffed', borderColor: '#b7eb8f' }}>
        <Text type="secondary">
          💡 <strong>Подсказка:</strong> Кликните на элементы легенды в графиках для скрытия/отображения данных.
          Это поможет лучше анализировать отдельные сегменты.
        </Text>
      </Card>
    </div>
  )
}

AppDetails.propTypes = {
  data: PropTypes.object,
  chartOptions: PropTypes.func.isRequired,
  loading: PropTypes.bool
}

export default AppDetails